import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import type { DestinationAffinityCopyResult } from '~/queries/customerCommunication/requestGetDestinationAffinityCopy';
import type {
  DestinationAffinityDraftResult,
  DestinationAffinityDraftSection,
} from '~/queries/customerCommunication/requestGetDestinationAffinityDraft';

// The copy endpoint rejects more offers than this (MAX_COPY_OFFER_IDS)
export const DESTINATION_AFFINITY_MAX_COPY_OFFER_IDS = 20;

/**
 * The offers to write the copy about: the email's offers top to bottom, without repeats, as many as the copy endpoint
 * accepts. In the template that's the destination's 15 hotels and 5 experiences, leaving out the hotels elsewhere.
 */
export function getDestinationAffinityCopyOfferIds(
  sections: Array<CustomerCommunication.CustomScheduleDynamicSection | DestinationAffinityDraftSection>,
): Array<string> {
  const offerIds = sortBy(sections, 'position').flatMap((section) =>
    section.type === 'OFFER' ? section.offerIds : [],
  );
  return uniq(offerIds.filter(Boolean)).slice(0, DESTINATION_AFFINITY_MAX_COPY_OFFER_IDS);
}

/** The hero images to choose from, the one in the draft first */
export function getDestinationAffinityHeaderImageOptions({
  draft,
  options,
}: DestinationAffinityDraftResult): Array<CustomerCommunication.DestinationAffinityHeaderImage> {
  const { headerDesktopImage, headerMobileImage } = draft.introSection;
  // a destination without hero images yet has none to choose from
  return headerDesktopImage
    ? [{ headerDesktopImage, headerMobileImage }, ...options.headerImages]
    : options.headerImages;
}

/** The subject lines and intros to choose from, the ones the copy recommends first */
export function getDestinationAffinityCopyOptions({
  draft,
  options,
}: DestinationAffinityCopyResult): Pick<
  CustomerCommunication.DestinationAffinityOptions,
  'subjectLines' | 'introCopy'
> {
  return {
    subjectLines: [{ subjectLine: draft.emailSubjectLine, preheader: draft.emailPreheader }, ...options.subjectLines],
    introCopy: [draft.introSection, ...options.introCopy],
  };
}

/**
 * Puts the copy's articles in place of the email's current ones, in order and wherever they've been moved to. Any extra
 * go in at their template position, e.g. between the offers of a freshly applied draft. Positions are renumbered to
 * match the order.
 */
export function replaceArticleSections<S extends { position: number }>(
  current: Array<S>,
  articles: Array<S>,
  isArticle: (section: S) => boolean,
): Array<S> {
  const articleIndexes = current.flatMap((section, index) => (isArticle(section) ? [index] : []));
  const next = [...current];
  articles.forEach((article, articleIndex) => {
    if (articleIndex < articleIndexes.length) {
      next[articleIndexes[articleIndex]] = article;
    } else {
      next.splice(article.position - 1, 0, article);
    }
  });
  return next.map((section, index) => ({ ...section, position: index + 1 }));
}
